import { useEffect, useState } from 'react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const downloadCV = () => {
    // Open the CV in a new tab
    window.open('https://drive.google.com/file/d/1SqBxv9AKWLkTc4cwMhdDCZbSdclkdhA3/view?usp=sharing', '_blank');
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Enhanced Background with Parallax Effect */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000 ease-out"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Stunning%20agricultural%20landscape%20with%20rolling%20green%20hills%2C%20golden%20wheat%20fields%2C%20sustainable%20farming%20terraces%2C%20warm%20earth%20tones%2C%20natural%20lighting%2C%20peaceful%20countryside%20setting%20with%20rich%20soil%20and%20organic%20farming%20elements%2C%20professional%20photography%20with%20warm%20brown%20and%20green%20color%20palette&width=1920&height=1080&seq=hero-enhanced-bg&orientation=landscape')`,
          transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px) scale(1.05)`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/70 via-yellow-800/60 to-green-800/70"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-amber-900/50 to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-4 h-4 bg-yellow-600/30 rounded-full animate-float" style={{ animationDelay: '0s' }}></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-amber-500/20 rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-40 left-20 w-3 h-3 bg-green-600/40 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-60 right-10 w-5 h-5 bg-yellow-700/25 rounded-full animate-float" style={{ animationDelay: '0.5s' }}></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        <div className={`transform transition-all duration-1200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}>
          {/* Enhanced Profile Image */}
          <div className="mb-10 flex justify-center">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-300 animate-pulse-glow"></div>
              <img
                src="https://nalediportfolio.my.canva.site/afiki-rajabo-chiwaya/_assets/media/7a29924206ac9670cddf234ad555b254.png"
                alt="Afiki Rajabo Chiwaya"
                className="relative w-36 h-36 rounded-full border-4 border-amber-200 shadow-2xl object-cover object-top transform group-hover:scale-110 transition-all duration-500 animate-scale-in"
                style={{ animationDelay: '0.5s' }}
              />
            </div>
          </div>

          {/* Enhanced Typography */}
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{ animationDelay: '0.8s' }}>
            <h1 className="text-5xl md:text-8xl font-bold mb-6 font-serif">
              <span className="block text-amber-100 drop-shadow-lg animate-fade-in-down" style={{ animationDelay: '1s' }}>Afiki Rajabo</span>
              <span className="block gradient-earth text-6xl md:text-7xl animate-fade-in-up" style={{ animationDelay: '1.2s' }}>Chiwaya</span>
            </h1>
          </div>
          
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{ animationDelay: '1.4s' }}>
            <p className="text-xl md:text-3xl text-yellow-100 mb-8 max-w-4xl mx-auto leading-relaxed font-medium">
              Agricultural Professional & Sustainable Farming Expert
            </p>
          </div>
          
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{ animationDelay: '1.6s' }}>
            <p className="text-lg md:text-xl text-amber-100/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              Ambitious and detail-oriented agricultural graduate with over 5 years of administrative experience, 
              specializing in sustainable farming practices and agricultural project coordination.
            </p>
          </div>
          
          {/* Enhanced Action Buttons */}
          <div className={`flex flex-col sm:flex-row gap-6 justify-center items-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} style={{ animationDelay: '1.8s' }}>
            <button
              onClick={scrollToContact}
              className="group bg-gradient-to-r from-amber-600 to-yellow-700 text-white px-10 py-5 rounded-full text-lg font-semibold hover:from-amber-700 hover:to-yellow-800 transform hover:scale-105 transition-all duration-400 shadow-xl whitespace-nowrap cursor-pointer btn-animate hover-lift"
            >
              <i className="ri-mail-line mr-3 group-hover:animate-bounce"></i>
              Get In Touch
            </button>
            <button
              onClick={downloadCV}
              className="group border-3 border-amber-200 text-amber-100 px-10 py-5 rounded-full text-lg font-semibold hover:bg-amber-100 hover:text-amber-800 transform hover:scale-105 transition-all duration-400 whitespace-nowrap cursor-pointer btn-animate hover-lift backdrop-blur-sm"
            >
              <i className="ri-download-line mr-3 group-hover:animate-bounce"></i>
              Download CV
            </button>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-amber-900/20 to-transparent pointer-events-none"></div>
    </section>
  );
};

export default Hero;
